import { useContext, Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import HomeAssociados from './areaAssociados/HomeAssociados';
import CardapioLanchonete from './areaAssociados/CardapioLanchonete';
import Carteirinha from './areaAssociados/Carteirinha';
import Espacos from './areaAssociados/Espacos';
import Financeiro from './areaAssociados/Financeiro';
import SaldoLanchoneteAssociados from './areaAssociados/SaldoLanchoneteAssociados';
import Login from './pages/Login';
import ResetPassword from './pages/ResetPassword';
import HomeColaboradores from './areaColaboradores/HomeColaboradores';
import DebitarPedido from './areaColaboradores/DebitarPedido';
import AdicionarSaldo from './areaColaboradores/RecargaSaldo';
import Caixa from './areaColaboradores/Caixa';
import AppContext from './api/AppContext';
import Perfil from './areaAssociados/Perfil';
import AcompanharPedido from './areaAssociados/AcompanharPedido';
import AcompanharPedidoStaff from './areaColaboradores/AcompanharPedidoStaff';
import MemberChat from './pages/MemberChat';
import RelatorioGestao from './areaAssociados/RelatorioGestao';
import GaleriaReformasAssociado from './areaAssociados/GaleriaReformas';
import Eventos from './areaAssociados/Eventos';
import BaseConhecimentoAssociado from './areaAssociados/BaseConhecimento';

const TelaInicial = lazy(() => import('./pages/TelaInicial'));
const DashboardAdmin = lazy(() => import('./pages/DashboardAdmin'));
const GestaoEspacos = lazy(() => import('./pages/GestaoEspacos'));
const Colaboradores = lazy(() => import('./pages/Colaboradores'));
const Associados = lazy(() => import('./pages/Associados'));
const Cardapio = lazy(() => import('./pages/pagesCardapio/Menu'));
const SaldoLanchonete = lazy(() => import('./pages/SaldoLanchonete'));
const FinanceiroClube = lazy(() => import('./pages/FinanceiroClube'));
const Relatorios = lazy(() => import('./pages/Relatorios'));
const StaffAtendimentos = lazy(() => import('./pages/StaffAtendimentos'));
const AdminAtendimentos = lazy(() => import('./pages/AdminAtendimentos'));
const GroupManager = lazy(() => import('./pages/admin/GroupManager'));
const AdministrarRelatorio = lazy(() => import('./pages/AdministrarRelatorio'));
const GaleriaReformasAdmin = lazy(() => import('./pages/GaleriaReformas'));
const RelatorioFinanceiro = lazy(() => import('./pages/RelatorioFinanceiro'));
const GestaoEventos = lazy(() => import('./pages/GestaoEventos'));
const GestaoAvisos = lazy(() => import('./pages/GestaoAvisos'));
const BaseConhecimento = lazy(() => import('./pages/BaseConhecimento'));
const PerfilAdmin = lazy(() => import('./pages/PerfilAdmin'));
const ConnectionHistory = lazy(() => import('./pages/ConnectionHistory'));

const Spinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100dvh', backgroundColor: '#F0F3FF', fontFamily: 'Montserrat' }}>
    <div style={{ textAlign: 'center' }}>
      <div style={{ width: '40px', height: '40px', border: '4px solid #e0e0e0', borderTopColor: '#1A3B7D', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 16px' }} />
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      <span style={{ color: '#888', fontSize: '14px' }}>Carregando...</span>
    </div>
  </div>
);

const ProtectedRoute = ({ children, requiredRole }) => {
  const { user, isAuthenticated, initialized, token } = useContext(AppContext);

  if (!initialized) return <Spinner />;

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  if (requiredRole) {
    const allowedRoles = Array.isArray(requiredRole)
      ? requiredRole
      : [requiredRole];

    let actualRole = user?.role;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.role) {
          actualRole = payload.role;
        }
      } catch (e) {}
    }

    if (!allowedRoles.includes(actualRole)) {
      return <Navigate to="/login" replace />;
    }
  }

  return <Suspense fallback={<Spinner />}>{children}</Suspense>;
};

const AppRoutes = () => {
  const { user, isAuthenticated } = useContext(AppContext);

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/reset-password/:token" element={<ResetPassword />} />
      <Route
        path="/"
        element={
          user && isAuthenticated ? (
            user.role === 'admin' ? (
              <Navigate to="/admin" replace />
            ) : user.role === 'member' ? (
              <Navigate to="/member/dashboard" replace />
            ) : user.role === 'staff' ? (
              <Navigate to="/staff/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          ) : (
            <Navigate to="/login" replace />
          )
        }
      />

      <Route path="/member/chat" element={<ProtectedRoute requiredRole="member"><MemberChat /></ProtectedRoute>} />
      <Route path="/member/chat/:conversationId" element={<ProtectedRoute requiredRole="member"><MemberChat /></ProtectedRoute>} />

      <Route path="/staff/atendimentos" element={<ProtectedRoute requiredRole="staff"><StaffAtendimentos /></ProtectedRoute>} />
      <Route path="/staff/atendimentos/:conversationId" element={<ProtectedRoute requiredRole={['staff', 'admin']}><StaffAtendimentos /></ProtectedRoute>} />

      <Route path="/admin/atendimentos" element={<ProtectedRoute requiredRole="admin"><AdminAtendimentos /></ProtectedRoute>} />
      <Route path="/admin/grupos" element={<ProtectedRoute requiredRole="admin"><GroupManager /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute requiredRole="admin"><ConnectionHistory /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><TelaInicial /></ProtectedRoute>} />
      <Route path="/admin/perfil" element={<ProtectedRoute requiredRole="admin"><PerfilAdmin /></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute requiredRole="admin"><DashboardAdmin /></ProtectedRoute>} />
      <Route path="/colaboradores" element={<ProtectedRoute requiredRole="admin"><Colaboradores /></ProtectedRoute>} />
      <Route path="/base-conhecimento" element={<ProtectedRoute requiredRole="admin"><BaseConhecimento /></ProtectedRoute>} />
      <Route path="/administrarrelatorio" element={<ProtectedRoute requiredRole="admin"><AdministrarRelatorio /></ProtectedRoute>} />
      <Route path="/galeria-reformas" element={<ProtectedRoute requiredRole="admin"><GaleriaReformasAdmin /></ProtectedRoute>} />
      <Route path="/relatorio-financeiro" element={<ProtectedRoute requiredRole="admin"><RelatorioFinanceiro /></ProtectedRoute>} />
      <Route path="/gestao-espacos" element={<ProtectedRoute requiredRole="admin"><GestaoEspacos /></ProtectedRoute>} />
      <Route path="/gestao-eventos" element={<ProtectedRoute requiredRole="admin"><GestaoEventos /></ProtectedRoute>} />
      <Route path="/gestao-avisos" element={<ProtectedRoute requiredRole="admin"><GestaoAvisos /></ProtectedRoute>} />
      <Route path="/associados" element={<ProtectedRoute requiredRole="admin"><Associados /></ProtectedRoute>} />
      <Route path="/cardapio" element={<ProtectedRoute requiredRole={["admin", "member", "staff"]}><Cardapio /></ProtectedRoute>} />
      <Route path="/saldolanchonete" element={<ProtectedRoute requiredRole="admin"><SaldoLanchonete /></ProtectedRoute>} />
      <Route path="/financeiroclube" element={<ProtectedRoute requiredRole="admin"><FinanceiroClube /></ProtectedRoute>} />
      <Route path="/relatorios" element={<ProtectedRoute requiredRole="admin"><Relatorios /></ProtectedRoute>} />
      <Route path="/servicoslanchonete" element={<ProtectedRoute requiredRole="admin"><HomeColaboradores /></ProtectedRoute>} />
      <Route path="/enviarpedido" element={<ProtectedRoute requiredRole={["admin", "staff", "member"]}><DebitarPedido /></ProtectedRoute>} />
      <Route path="/staff/adicionar-saldo" element={<ProtectedRoute requiredRole={["admin", "staff"]}><AdicionarSaldo /></ProtectedRoute>} />
      <Route path="/staff/acompanhar-pedido-staff" element={<ProtectedRoute requiredRole={["admin", "staff"]}><AcompanharPedidoStaff /></ProtectedRoute>} />
      <Route path="/staff/caixa" element={<ProtectedRoute requiredRole={["admin", "staff"]}><Caixa /></ProtectedRoute>} />

      <Route path="/member/base-conhecimento" element={<ProtectedRoute requiredRole="member"><BaseConhecimentoAssociado /></ProtectedRoute>} />
      <Route path="/member/dashboard" element={<ProtectedRoute requiredRole="member"><HomeAssociados /></ProtectedRoute>} />
      <Route path="/member/relatoriogestao" element={<ProtectedRoute requiredRole="member"><RelatorioGestao /></ProtectedRoute>} />
      <Route path="/member/carteirinha" element={<ProtectedRoute requiredRole="member"><Carteirinha /></ProtectedRoute>} />
      <Route path="/member/espacos" element={<ProtectedRoute requiredRole="member"><Espacos /></ProtectedRoute>} />
      <Route path="/member/financeiro" element={<ProtectedRoute requiredRole="member"><Financeiro /></ProtectedRoute>} />
      <Route path="/member/saldo" element={<ProtectedRoute requiredRole="member"><SaldoLanchoneteAssociados /></ProtectedRoute>} />
      <Route path="/member/acompanhar-pedido" element={<ProtectedRoute requiredRole="member"><AcompanharPedido /></ProtectedRoute>} />
      <Route path="/member/perfil" element={<ProtectedRoute requiredRole="member"><Perfil /></ProtectedRoute>} />
      <Route path="/member/eventos" element={<ProtectedRoute requiredRole="member"><Eventos /></ProtectedRoute>} />
      <Route path="/member/galeria-reformas" element={<ProtectedRoute requiredRole="member"><GaleriaReformasAssociado /></ProtectedRoute>} />
      <Route path="/member/cardapio" element={<ProtectedRoute requiredRole="member"><CardapioLanchonete /></ProtectedRoute>} />

      <Route path="/staff/dashboard" element={<ProtectedRoute requiredRole="staff"><HomeColaboradores /></ProtectedRoute>} />

      <Route path="/unauthorized" element={<div>Acesso não autorizado</div>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

export default AppRoutes;
